import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { CountriesHttpService, CountryISOCode } from 'app/api';
import { CountryNameService } from 'app/services/country-name.service';
import { Subject, debounceTime, map, shareReplay, startWith, takeUntil } from 'rxjs';

@Component({
	selector: 'app-country-selector',
	templateUrl: './country-selector.component.html',
	styleUrls: ['./country-selector.component.scss'],
	standalone: false,
})
export class CountrySelectorComponent implements OnInit, OnDestroy {
	constructor(
		private countryService: CountriesHttpService,
		protected countryNameService: CountryNameService,
		protected formBuilder: FormBuilder
	) {}
	@Input() initialdoesOperateOutsideOwnCountry: boolean;
	@Input() initialSelectedCountries: CountryISOCode[];

	searchForm: FormGroup;
	isSearchActive = false;
	allCountries: { code: string; name: string }[] = [];
	filteredCountries: { code: string; name: string }[] = [];
	private destroy$ = new Subject<void>();

	get selectedCountries(): FormGroup {
		return this.searchForm.get('selectedCountries') as FormGroup;
	}
	get doesOperateOutsideOwnCountry(): AbstractControl {
		return this.searchForm.get('doesOperateOutsideOwnCountry');
	}

	ngOnInit(): void {
		this.initializeForm();
		this.loadCountries();
		this.subscribeToChanges();
	}

	private initializeForm(): void {
		this.searchForm = this.formBuilder.group(
			{
				doesOperateOutsideOwnCountry: [this.initialdoesOperateOutsideOwnCountry, [Validators.required]],
				search: [''],
				selectedCountries: this.formBuilder.group({}),
			},
			{ validators: [this.requireCountryIfOutsideMarket()] }
		);
	}

	private loadCountries(): void {
		this.countryService
			.v1CountriesGet()
			.pipe(
				map(countries =>
					countries
						.map(country => ({
							code: country,
							name: this.countryNameService.getNameByCode(country),
						}))
						.sort((a, b) => a.name.localeCompare(b.name))
				),
				shareReplay(1)
			)
			.subscribe(countries => {
				this.allCountries = countries;
				this.filteredCountries = countries;
				const countryControls = countries.reduce((controls, country) => {
					const isSelected = this.initialSelectedCountries?.includes(country.code) ?? false;
					controls[country.code] = new FormControl(isSelected);
					return controls;
				}, {});

				this.searchForm.setControl('selectedCountries', this.formBuilder.group(countryControls));
			});
	}

	private subscribeToChanges(): void {
		this.doesOperateOutsideOwnCountry.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(value => {
			if (!value) {
				this.clearSelectedCountries();
			}
		});

		this.searchForm
			.get('search')
			.valueChanges.pipe(startWith(''), debounceTime(300), takeUntil(this.destroy$))
			.subscribe(value => this.filterCountries(value));
	}

	filterCountries(searchTerm: string): void {
		const lowerCaseSearchTerm = searchTerm.toLowerCase();
		this.filteredCountries = this.allCountries.filter(country =>
			country.name.toLowerCase().includes(lowerCaseSearchTerm)
		);
	}

	getCheckedCountries(): { code: string; name: string }[] {
		const selectedControls = this.selectedCountries.value;
		return this.allCountries.filter(country => selectedControls[country.code]);
	}

	clearSelectedCountries(): void {
		Object.keys(this.selectedCountries.controls).forEach(key => {
			this.selectedCountries.get(key)?.setValue(false);
		});
	}

	removeCountry(code: string): void {
		const control = this.selectedCountries.get(code);
		if (control) {
			control.setValue(false);
		}
	}

	handleSearchOnFocus() {
		this.isSearchActive = true;
	}

	handleSearchOnBlur(event: FocusEvent) {
		const relatedTarget = event.relatedTarget as HTMLElement;
		if (relatedTarget && (relatedTarget.id === 'country-list' || relatedTarget.closest('#country-list'))) {
			return;
		}
		this.isSearchActive = false;
	}

	requireCountryIfOutsideMarket(): (form: FormGroup) => ValidationErrors | null {
		return (form: FormGroup): ValidationErrors | null => {
			const isOutside = form.get('doesOperateOutsideOwnCountry')?.value;
			const selectedCountries = form.get('selectedCountries')?.value;
			const hasSelectedCountries = selectedCountries && Object.values(selectedCountries).some(val => val === true);
			if (isOutside && !hasSelectedCountries) {
				return { requireCountryIfOutsideMarket: true };
			}

			return null;
		};
	}

	validateForm() {
		this.searchForm.markAllAsTouched();
		return this.searchForm.valid;
	}

	returnData(): { associatedCountries: CountryISOCode[]; doesOperateOutsideOwnCountry: boolean } {
		return {
			associatedCountries: Object.keys(this.selectedCountries.value)
				.filter(code => this.selectedCountries.value[code])
				.map(code => code as CountryISOCode),
			doesOperateOutsideOwnCountry: this.doesOperateOutsideOwnCountry.value,
		};
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}
}
